const DebugTag = '[communication-service]';
const Storage = require('utils/storage');
const PersistenceConfig = require('utils/persistence-config');

const internals = {};

internals.isE2EMockEnabled = () => {

    if (typeof window === 'undefined') {
        return false;
    }

    if (process.env.PLAYWRIGHT_E2E === 'true') {
        return false;
    }

    const storageValue = typeof Storage.getItem === 'function'
        ? Storage.getItem(PersistenceConfig.keys.e2e.mockCommunication)
        : null;
    const twilioMockEnabled = typeof Storage.getItem === 'function'
        ? Storage.getItem(PersistenceConfig.keys.e2e.mockTwilio)
        : null;

    if (twilioMockEnabled === '1') {
        return false;
    }

    return storageValue === '1'
        || new URLSearchParams(window.location.search).get('e2eMockCommunication') === '1';
};

internals.mockResult = (result = undefined) => Promise.resolve(result);

internals.emptyNormalizedList = () => ({
    entities: {},
    result: []
});

internals.emptyNormalizedObject = () => ({
    entities: {},
    result: null
});

internals.createMockCommunicationProvider = () => ({
    initialize: () => internals.mockResult(),
    shutdown: () => internals.mockResult(),
    destroyAccessManager: () => internals.mockResult(),
    recoverConnection: () => internals.mockResult(),
    ensureConnection: () => internals.mockResult(),
    updateToken: () => internals.mockResult(),
    startListeningMessages: () => internals.mockResult(),
    stopListeningMessages: () => internals.mockResult(),
    getChannels: () => internals.mockResult(internals.emptyNormalizedList()),
    getDMChannelByUsers: () => internals.mockResult(internals.emptyNormalizedObject()),
    getConversationLastMessage: () => internals.mockResult({
        entities: {},
        result: {}
    }),
    getAllConversationsLastMessage: () => internals.mockResult({
        entities: {},
        result: {}
    }),
    getConversation: () => internals.mockResult(internals.emptyNormalizedObject()),
    getHasPrevPage: () => internals.mockResult({ result: false }),
    getMessages: () => internals.mockResult(internals.emptyNormalizedList()),
    getMoreMessages: () => internals.mockResult(internals.emptyNormalizedList()),
    sendMessage: ({ clientMessageId }) => internals.mockResult({ messageIndex: 0, clientMessageId }),
    updateMessageBody: () => internals.mockResult({ message: null }),
    removeMessage: () => internals.mockResult(),
    markMessageAsLastRead: () => internals.mockResult()
});

const createCommunicationService = (twilioProvider) => ({
    initialize: (options) => twilioProvider.initialize(options),
    shutdown: () => twilioProvider.shutdown(),
    destroySession: () => twilioProvider.destroyAccessManager(),
    recoverConnection: () => twilioProvider.recoverConnection(),
    ensureConnection: (options) => twilioProvider.ensureConnection(options),
    updateToken: (payload = {}) => {
        const token = payload.token || payload.result?.token || payload.payload?.result?.token;

        return twilioProvider.updateToken(token ? { token } : payload);
    },
    startListeningMessages: ({ listeners }) => twilioProvider.startListeningMessages({ listeners }),
    stopListeningMessages: () => twilioProvider.stopListeningMessages(),
    getChannels: () => twilioProvider.getChannels(),
    getDMChannelByUsers: (uId1, uId2) => twilioProvider.getDMChannelByUsers(uId1, uId2),
    getConversationLastMessage: ({ channelSid }) => twilioProvider.getConversationLastMessage({ channelSid }),
    getAllConversationsLastMessage: () => twilioProvider.getAllConversationsLastMessage(),
    getConversation: ({ channelSid }) => twilioProvider.getConversation({ channelSid }),
    getHasPrevPage: ({ channelSid }) => twilioProvider.getHasPrevPage({ channelSid }),
    getMessages: ({ channelSid }) => twilioProvider.getMessages({ channelSid }),
    getMoreMessages: ({ channelSid }) => twilioProvider.getMoreMessages({ channelSid }),
    sendMessage: ({ channelSid, message, clientMessageId }) => twilioProvider.sendMessage({
        channelSid, message, ...(clientMessageId ? { clientMessageId } : {})
    }),
    updateMessageBody: ({ sid, body }) => twilioProvider.updateMessageBody({ sid, body }),
    removeMessage: ({ messageSid }) => twilioProvider.removeMessage({ messageSid }),
    markMessageAsLastRead: ({ channelSid, messageIndex = null }) => twilioProvider.markMessageAsLastRead({ channelSid, messageIndex }),
    logTag: DebugTag
});

createCommunicationService.fromContext = (context) => {

    if (internals.isE2EMockEnabled()) {
        return createCommunicationService(internals.createMockCommunicationProvider());
    }

    return createCommunicationService(context.api.twilio);
};

module.exports = createCommunicationService;
